/**
 * WizardTimeline.tsx
 *
 * Collapsible 11-step timeline for the presentation wizard.
 *
 * Pattern:
 *   - When in SETUP (steps 1-5): show 5 setup steps + a single collapsed
 *     "Build" pill at the end showing "6 steps" dimmed.
 *   - When in BUILD (steps 1-6): show a single collapsed "✓ Setup" pill at the
 *     start (green, clickable to return to setup step 1) + the 6 build steps.
 *
 * The collapsed pill is a compact rounded badge. Clicking the setup pill
 * navigates back to setup step 1.
 */

import styles from './WizardTimeline.module.css';

const SETUP_LABELS = ['City', 'Template', 'Theme', 'Providers', 'Details'];
const BUILD_LABELS = ['Questions', 'Plan', 'Slides', 'Images', 'Costs', 'Generate'];

interface Props {
  activeBar: 'setup' | 'build';
  setupStep: number;
  buildStep: number;
  highestSetupStep: number;
  highestBuildStep: number;
  onStepClick: (bar: 'setup' | 'build', step: number) => void;
}

export const WizardTimeline = ({
  activeBar,
  setupStep,
  buildStep,
  highestSetupStep,
  highestBuildStep,
  onStepClick,
}: Props) => {
  // ── Helpers ──────────────────────────────────────────────────────────────────

  /**
   * Determine if a step within a bar is completed.
   * A step is completed when the current position is strictly past it.
   */
  const isSetupCompleted = (stepNum: number): boolean => {
    if (activeBar === 'build') return true; // All setup completed when in build bar
    return stepNum < setupStep;
  };

  const isBuildCompleted = (stepNum: number): boolean => {
    if (activeBar === 'setup') return false;
    return stepNum < buildStep;
  };

  const isSetupCurrent = (stepNum: number): boolean =>
    activeBar === 'setup' && stepNum === setupStep;

  const isBuildCurrent = (stepNum: number): boolean =>
    activeBar === 'build' && stepNum === buildStep;

  /**
   * A setup step is clickable if it has been visited (highestSetupStep tracks
   * the furthest step reached) and is not the current step.
   */
  const isSetupClickable = (stepNum: number): boolean => {
    if (activeBar === 'build') return true; // All setup steps clickable from build
    return stepNum < setupStep && stepNum <= highestSetupStep;
  };

  const isBuildClickable = (stepNum: number): boolean => {
    if (activeBar === 'setup') return false;
    return stepNum < buildStep && stepNum <= highestBuildStep;
  };

  // ── Step renderer ─────────────────────────────────────────────────────────

  const renderStep = (
    label: string,
    stepNum: number,
    bar: 'setup' | 'build',
    completed: boolean,
    current: boolean,
    clickable: boolean,
  ) => (
    <button
      key={`${bar}-${stepNum}`}
      type="button"
      className={[
        styles.step,
        completed ? styles.completed : '',
        current ? styles.current : '',
        clickable ? styles.clickable : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={() => clickable && onStepClick(bar, stepNum)}
      disabled={!clickable}
      title={label}
      aria-current={current ? 'step' : undefined}
    >
      <div className={styles.dot}>
        {completed ? <span className={styles.check}>&#10003;</span> : null}
      </div>
      <span className={styles.label}>{label}</span>
    </button>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  if (activeBar === 'setup') {
    return (
      <div className={styles.timeline}>
        {/* Active setup steps */}
        <div className={styles.group}>
          {SETUP_LABELS.map((label, i) => {
            const stepNum = i + 1;
            return (
              <div key={label} className={styles.stepWrapper}>
                {renderStep(
                  label,
                  stepNum,
                  'setup',
                  isSetupCompleted(stepNum),
                  isSetupCurrent(stepNum),
                  isSetupClickable(stepNum),
                )}
                {i < SETUP_LABELS.length - 1 && (
                  <div
                    className={[
                      styles.connector,
                      isSetupCompleted(stepNum) ? styles.connectorDone : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.divider} aria-hidden />

        {/* Collapsed build pill */}
        <div className={styles.collapsedPill} aria-label="Build phase: 6 steps (not started yet)">
          <span className={styles.collapsedPillIcon}>&#x25BA;</span>
          <span className={styles.collapsedPillLabel}>Build</span>
          <span className={styles.collapsedPillCount}>6 steps</span>
        </div>
      </div>
    );
  }

  // activeBar === 'build'
  return (
    <div className={styles.timeline}>
      {/* Collapsed setup pill — all done, clickable */}
      <button
        type="button"
        className={styles.collapsedPillDone}
        onClick={() => onStepClick('setup', 1)}
        title="Return to Setup (step 1)"
        aria-label="Setup complete — click to go back"
      >
        <span className={styles.collapsedPillDoneCheck}>&#10003;</span>
        <span className={styles.collapsedPillDoneLabel}>Setup</span>
      </button>

      <div className={styles.divider} aria-hidden />

      {/* Active build steps */}
      <div className={styles.group}>
        {BUILD_LABELS.map((label, i) => {
          const stepNum = i + 1;
          return (
            <div key={label} className={styles.stepWrapper}>
              {renderStep(
                label,
                stepNum,
                'build',
                isBuildCompleted(stepNum),
                isBuildCurrent(stepNum),
                isBuildClickable(stepNum),
              )}
              {i < BUILD_LABELS.length - 1 && (
                <div
                  className={[
                    styles.connector,
                    isBuildCompleted(stepNum) ? styles.connectorDone : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
